import React from 'react';
import { useTranslation } from 'react-i18next';
import { isBefore } from 'date-fns';

import styles from './styles/CustomerDetails.module.scss';

// Components.
import CustomerCommunicationChoices from '../Contact/CustomerCommunicationChoices';
import UpdateMyInfo from './UpdateMyInfo';
import ExternalMandate from './ExternalMandate';
import Accordion from '../Common/Accordion';
import LoggedinUserDetails from './LoggedinUserDetails';
import MyInfoIban from './MyInfoIban';
// Actions.
import { fetchLedgers } from '../../actions';
// Types.
import { CustomerProps } from '../../types/customer';
import { CustomerAddress } from '../../types/address';
import { Ledger } from '../../types/billing';
// Utils.
import { generateAddress } from '../../utils/address';
import { formatDate } from '../../utils/billing';
import { gaEventIfAllowed } from '../../utils/googleAnalytics';
import { hideUpdateInfo } from './MyInfo';

const prepName = (nameArray: (string | number)[]): string => {
  let customerName = '';
  nameArray.map((part: string | number | undefined): undefined => {
    if (part) {
      customerName = `${customerName} ${part}`;
    }
    return undefined;
  });

  return customerName;
};
// Even though due to other types we know that the props sent are all strings, it doesn't like that so
// (props: CustomerProps) => string means string in this case.
const generateName: (props: CustomerProps, companyName: string) => string = (props: CustomerProps) => {
  const { customer } = props;
  let customerName = '';
  const nameArray = [customer.userFirstName, customer.userLastName];
  customerName = prepName(nameArray);
  customerName = customer.userName;

  return customerName;
};

type AddressDetails = {
  address: CustomerAddress;
  upcomingAddress: string;
};

const getCorrectAddress = (addresses: CustomerAddress[]): AddressDetails => {
  let currentAddress = addresses[0];
  let upcomingAddress = '';
  addresses.forEach((address) => {
    if (isBefore(new Date(address.fromDate), new Date())) {
      currentAddress = address;
    } else {
      upcomingAddress = address.fromDate;
    }
  });

  const addressDetails = {
    address: currentAddress,
    upcomingAddress: upcomingAddress,
  };

  return addressDetails;
};

const CustomerDetails: React.FunctionComponent<CustomerProps> = (props: CustomerProps) => {
  const { t } = useTranslation();

  const { customer, manageContracts, type } = props;

  const [loadLedgers, setLoadLedgers] = React.useState<boolean>(false);
  const [updateUserDetails, setUpdateUserDetails] = React.useState<boolean>(false);
  const [ledgers, setLedgers] = React.useState<Ledger[]>([]);

  let companyName = '';
  if (type === 'child') {
    companyName = t(`common:company.${customer.endpoint}`);
  }
  const name = generateName(props, companyName);

  // If the user is not a child user it will have an email and mobile number.
  let customerId: number | undefined, mobileNumber, email, address, zipCode, city, country, postBox, endpoint: string | undefined;
  let upcomingAddress = '';

  if (customer) {
    let currentAddress = customer.userAddress[0];

    if (customer.userAddress.length > 1) {
      const addressDetails = getCorrectAddress(customer.userAddress);
      currentAddress = addressDetails.address;
      upcomingAddress = addressDetails.upcomingAddress;
    }

    customerId = customer.userId;
    mobileNumber = customer.userNumber;
    email = customer.userEmail;
    if (currentAddress) {
      address = generateAddress(currentAddress, true);
      zipCode = currentAddress['zipCode'];
      city = currentAddress['cityName'];
      country = currentAddress['countryName'];
      postBox = currentAddress['postOfficeBox'];
    }
    endpoint = customer.endpoint;
  }

  React.useEffect(() => {
    let unMounted = false;

    // Create an scoped async function in the hook
    async function fetchUserLedgers(): Promise<void> {
      if (customerId && customerId !== 0) {
        const fetchedLedgers = await fetchLedgers(customerId, customerId);

        if (fetchedLedgers && !unMounted) {
          setLedgers(fetchedLedgers);
        }
      }
    }
    // Execute the created function directly
    if (loadLedgers) {
      fetchUserLedgers();
    }

    return (): void => {
      unMounted = true;
    };
  }, [customer, loadLedgers]);

  const title = (
    <>
      {name} | {customerId}
      <br />
      {address ? `${address?.trim()},` : null} {zipCode} {city}
    </>
  );

  return (
    <Accordion
      className={type === 'child' ? 'customerChild' : 'customer'}
      title={title}
      icon={true}
      open={manageContracts}
      onClick={(): void => {
        if (!manageContracts) {
          setLoadLedgers(true);
        }
      }}
    >
      <>
        {customer?.loginEmail && !manageContracts && <LoggedinUserDetails customer={customer} />}
        {customerId !== 0 && (
          <>
            <p>{t(manageContracts ? `myInfo:customerInfo.ingress${endpoint}ManageContracts` : `myInfo:customerInfo.ingress${endpoint}`)}</p>
            <div className={styles.address}>
              <div className="phone detail col-3">
                <label> {t('common:general.phone')} </label>
                <p>{mobileNumber}</p>
              </div>
              <div className="email detail col-9">
                <label> {t('common:general.email')} </label>
                <p>{email}</p>
              </div>
              {address && (
                <div className="address detail col-3">
                  <label> {t('common:general.address')} </label>
                  <p> {address} </p>
                </div>
              )}
              {postBox && (
                <div className="postbox detail col-3">
                  <label> {t('common:general.postbox')} </label>
                  <p> {postBox} </p>
                </div>
              )}
              <div className="zipcode detail col-3">
                <label> {t('common:general.zipcode')} </label>
                <p> {zipCode} </p>
              </div>
              <div className="city detail col-3">
                <label> {t('common:general.postalArea')} </label>
                <p> {city} </p>
              </div>
              <div className="country detail col-3">
                <label> {t('common:general.country')} </label>
                <p> {country} </p>
              </div>
              {upcomingAddress !== '' && (
                <div className="upcoming detail col-3">
                  <label> {t('common:general.upcoming')} </label>
                  <p> {formatDate(upcomingAddress)} </p>
                </div>
              )}
            </div>
            <Accordion
              title={t('myInfo:customerInfo.updateDetails')}
              onClick={() =>
                gaEventIfAllowed({
                  category: 'Omien tietojen päivittäminen',
                  action: 'Muokkaa yhteystietoja',
                })
              }
            >
              <UpdateMyInfo customer={customer} setUpdateUsers={setUpdateUserDetails} />
            </Accordion>

            {!manageContracts && !hideUpdateInfo && <MyInfoIban customer={customer} ledgers={ledgers} />}
          </>
        )}
        {type === 'parent' && !manageContracts && <ExternalMandate />}
        {customerId && !manageContracts && type === 'parent' && customer?.customerType?.toLowerCase() === 'private' && !hideUpdateInfo && (
          <CustomerCommunicationChoices userId={customerId} />
        )}
      </>
    </Accordion>
  );
};

export default CustomerDetails;
